import { useEffect, useState } from "react";

import { useIsAuthenticated } from "@azure/msal-react";

import { hasValidAppToken } from "@/utils/appToken";

/** How often to re-check the app-token `exp` (external lineage has no events). */
const APP_TOKEN_RECHECK_MS = 15_000;

/**
 * Reactive "is the user's session still alive" signal, usable anywhere in the
 * tree — including app-level UI that lives outside `ProtectedRoute` and even
 * outside the router (e.g. the bundle-upload persistent toast, MDRS-267).
 *
 * Two auth lineages must both count as active:
 *  - Internal staff carry an MSAL account (`useIsAuthenticated()`, reactive to
 *    MSAL login/logout events).
 *  - External OTP/expert users have no MSAL account but a valid app-issued JWT
 *    (`hasValidAppToken()`, checked against its own `exp`).
 *
 * When neither holds, the session has ended (login expired or a 401 drove a
 * logout) and any live UI that assumes an active session should stand down.
 *
 * Reads MSAL context directly rather than going through `useAuth`: `useAuth`
 * also calls `useNavigate`/`useDispatch`, which would throw when rendered
 * outside the router — exactly the case this hook is meant to serve.
 *
 * The app-token path has no event to react to, so it's polled on a short
 * interval (and on tab refocus) and mirrored into state. `setAppTokenValid`
 * only re-renders when the boolean actually flips, so internal MSAL sessions
 * (where it stays `false`) pay no render cost.
 */
export const useIsSessionActive = (): boolean => {
  const isAuthenticated = useIsAuthenticated();
  const [appTokenValid, setAppTokenValid] = useState(() => hasValidAppToken());

  useEffect(() => {
    const check = () => setAppTokenValid(hasValidAppToken());
    check();
    const intervalId = setInterval(check, APP_TOKEN_RECHECK_MS);
    document.addEventListener("visibilitychange", check);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", check);
    };
  }, []);

  return isAuthenticated || appTokenValid;
};
